/**
 * Plan Preview — pick a curriculum + level + day and see that day's exercises
 * (plus the curriculum's placement tests) listed and handled exactly like the
 * student website's exercises page. Read-only: clicking a card opens the question
 * flow, but there is no answering, recording, or submitting.
 */
import {useEffect, useMemo, useState} from 'react';
import {Alert, Empty, Select, Space, Spin, Typography} from 'antd';

import {CurriculumsService} from '../Curriculums/CurriculumsService';
import {LevelsService} from '../Level/LevelsService';
import {PlansService} from '../Plans/PlansService';
import type {CurriculumUI} from '../Curriculums/CurriculumTypes';
import type {LevelUI} from '../Level/LevelTypes';

import {DashboardExercise, MCQModal, QuestionModal} from './PikadoComponents';
import TypedAnswerModal from './TypedAnswerModal';
import {useMediaLoader} from './useMediaLoader';
import {useTestProcessor, type PreviewPlacementTest} from './useTestProcessor';
import {useExercisePreview} from './useExercisePreview';
import {
  formatComprehensionTitleText,
  getBlankGroups,
  getChoicesForQuestion,
  hasMultipleBlanks,
  processQuestionText,
  shouldUseMCQModal,
  shouldUseTypedAnswerModal,
} from './helpers';

import '@PikaDoApp/pikado-web-dsm/dist/dist/styles.css';

const {Title, Text} = Typography;

// Pikado main brand uses the gradient styling in the DSM.
const BUSINESS_COLOR = true;

const curriculumsService = new CurriculumsService();
const levelsService = new LevelsService();
const plansService = new PlansService();

export const PlanPreviewPage = () => {
  // Selection data
  const [curriculums, setCurriculums] = useState<CurriculumUI[]>([]);
  const [levels, setLevels] = useState<LevelUI[]>([]);
  const [selectionLoading, setSelectionLoading] = useState(true);

  const [curriculumId, setCurriculumId] = useState<string>();
  const [levelId, setLevelId] = useState<string>();

  // Curriculum's placement tests (listed alongside the day's exercises).
  const [placementTests, setPlacementTests] = useState<
    PreviewPlacementTest[] | undefined
  >(undefined);

  // Resolved plan
  const [plan, setPlan] = useState<any>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  // Day + raw exercises (media is signed on-demand, like the student app).
  const [day, setDay] = useState<number>(); // 0-based
  const [dayExercises, setDayExercises] = useState<any[] | undefined>(
    undefined,
  );
  const [exercisesLoading, setExercisesLoading] = useState(false);

  const {loadMediaUrl} = useMediaLoader();
  const {tests} = useTestProcessor(dayExercises, placementTests);
  const {
    processedQuestions,
    selectedQuestion,
    isQuestionModalOpen,
    isMCQModalOpen,
    isTypedAnswerModalOpen,
    isPreviewMode,
    previewMedia,
    isCreatingSession,
    handleCardClick,
    moveToNextQuestion,
    goToQuestionFromPreview,
    closeModals,
  } = useExercisePreview({tests, loadMediaUrl});

  // Load curriculums + levels once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, l] = await Promise.all([
          curriculumsService.getCurriculums(),
          levelsService.getLevels(),
        ]);
        if (cancelled) return;
        setCurriculums(c ?? []);
        setLevels(l ?? []);
      } catch (error) {
        console.error(
          '[PlanPreview] Failed to load curriculums/levels:',
          error,
        );
      } finally {
        if (!cancelled) setSelectionLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the selected curriculum's placement tests.
  useEffect(() => {
    setPlacementTests(undefined);
    if (!curriculumId) return;

    let cancelled = false;
    (async () => {
      try {
        const curriculum: any =
          await curriculumsService.getCurriculumById(curriculumId);
        if (cancelled) return;
        const list: PreviewPlacementTest[] = (curriculum?.placementTest ?? [])
          .filter((pt: any) => pt && (pt._id || pt.id))
          .map((pt: any) => ({
            _id: pt._id || pt.id,
            name: pt.name,
            description: pt.description,
            thumbnail: pt.thumbnail,
            createdBy: pt.createdBy,
          }));
        setPlacementTests(list);
      } catch (error) {
        console.error('[PlanPreview] Failed to load placement tests:', error);
        if (!cancelled) setPlacementTests([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [curriculumId]);

  // Resolve the plan whenever both curriculum and level are chosen.
  useEffect(() => {
    setPlan(null);
    setPlanError(null);
    setDay(undefined);
    setDayExercises(undefined);
    if (!curriculumId || !levelId) return;

    let cancelled = false;
    setPlanLoading(true);
    (async () => {
      try {
        const result = await plansService.getPlanByLevelAndCurriculum(
          levelId,
          curriculumId,
        );
        if (cancelled) return;
        if (!result) {
          setPlanError(
            'No plan exists for this curriculum and level combination.',
          );
          return;
        }
        setPlan(result);
      } catch (error) {
        console.error('[PlanPreview] Failed to load plan:', error);
        if (!cancelled) setPlanError('Failed to load the plan. Please retry.');
      } finally {
        if (!cancelled) setPlanLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [curriculumId, levelId]);

  const dayCount: number = useMemo(
    () => plan?.duration || plan?.dailyExercises?.length || 0,
    [plan],
  );

  // Load the day's exercises (raw — no pre-signing) whenever a day is selected.
  useEffect(() => {
    setDayExercises(undefined);
    if (!plan?.id || day === undefined) return;

    let cancelled = false;
    setExercisesLoading(true);
    (async () => {
      try {
        const raw = await plansService.getDailyExercisesByPlanAndDay(
          plan.id,
          day,
        );
        if (!cancelled) setDayExercises(raw ?? []);
      } catch (error) {
        console.error('[PlanPreview] Failed to load daily exercises:', error);
        if (!cancelled) setDayExercises([]);
      } finally {
        if (!cancelled) setExercisesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan, day]);

  const curriculumOptions = curriculums.map(c => ({
    label: c.name,
    value: c.id,
  }));
  const levelOptions = levels.map(l => ({label: l.name, value: l.id}));
  const dayOptions = Array.from({length: dayCount}, (_, index) => ({
    label: `Day ${index + 1}`,
    value: index,
  }));

  const isLastQuestion =
    (selectedQuestion?.currentQuestionIndex ?? 0) >=
    (selectedQuestion?.totalQuestionsInTest ?? 1) - 1;
  const nextLabel = isLastQuestion ? 'Finish' : 'Next question';

  return (
    <div style={{padding: 24}}>
      <Title level={3}>Plan Preview</Title>
      <Text type="secondary">
        Pick a curriculum and level, then a day, to preview the exercises and
        tests a student would see.
      </Text>

      <Space size="middle" wrap style={{marginTop: 16, marginBottom: 24}}>
        <Select
          showSearch
          placeholder="Select curriculum"
          style={{width: 260}}
          loading={selectionLoading}
          options={curriculumOptions}
          value={curriculumId}
          onChange={setCurriculumId}
          optionFilterProp="label"
        />
        <Select
          showSearch
          placeholder="Select level"
          style={{width: 220}}
          loading={selectionLoading}
          options={levelOptions}
          value={levelId}
          onChange={setLevelId}
          optionFilterProp="label"
        />
        <Select
          placeholder="Select day"
          style={{width: 160}}
          disabled={!plan || dayCount === 0}
          loading={planLoading}
          options={dayOptions}
          value={day}
          onChange={setDay}
        />
      </Space>

      {planError && (
        <Alert
          type="info"
          showIcon
          message={planError}
          style={{marginBottom: 24}}
        />
      )}

      {plan && day !== undefined && (
        <>
          {exercisesLoading ? (
            <div
              style={{display: 'flex', justifyContent: 'center', padding: 48}}>
              <Spin tip="Loading exercises..." />
            </div>
          ) : processedQuestions.length === 0 ? (
            <Empty description="No exercises or tests for this selection." />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 30,
              }}>
              {processedQuestions.map(card => (
                <DashboardExercise
                  key={card.id}
                  id={card.id}
                  name={card.name ?? card.title ?? 'Untitled Test'}
                  summary={card.summary ?? card.description ?? ''}
                  url={
                    card.isVideo
                      ? (card.image ?? card.url ?? '')
                      : (card.url ?? card.image ?? '')
                  }
                  isVideo={card.isVideo ?? false}
                  businessColor={BUSINESS_COLOR}
                  actionText={card.isVideo ? 'Watch Video' : 'Answer Question'}
                  onClick={() => handleCardClick(card.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Loading overlay while a placement test's questions are fetched. */}
      {isCreatingSession && (
        <div style={{display: 'flex', justifyContent: 'center', padding: 24}}>
          <Spin tip="Loading test..." />
        </div>
      )}

      {/* Question preview / recording-question screen */}
      {selectedQuestion && (
        <QuestionModal
          open={isQuestionModalOpen}
          businessColor={BUSINESS_COLOR}
          onClose={closeModals}
          title={
            isPreviewMode && previewMedia?.isVideo
              ? 'Watch the video, then go to the questions.'
              : selectedQuestion.name || 'Question'
          }
          horizontal={isPreviewMode}
          videoUrl={
            isPreviewMode && previewMedia?.isVideo
              ? previewMedia.videoUrl
              : !isPreviewMode &&
                  selectedQuestion.hasOwnMedia &&
                  selectedQuestion.isVideo &&
                  selectedQuestion.url &&
                  selectedQuestion.url !== 'undefined'
                ? selectedQuestion.url
                : undefined
          }
          imageUrl={
            isPreviewMode && previewMedia?.thumbnailUrl
              ? previewMedia.thumbnailUrl
              : !isPreviewMode &&
                  selectedQuestion.hasOwnMedia &&
                  !selectedQuestion.isVideo &&
                  selectedQuestion.url &&
                  selectedQuestion.url !== 'undefined'
                ? selectedQuestion.url
                : undefined
          }
          SecondaryText={
            isPreviewMode
              ? `${selectedQuestion.totalQuestionsInTest || 1} Questions`
              : selectedQuestion.questionText ||
                selectedQuestion.description ||
                'No description available'
          }
          buttonText={isPreviewMode ? 'Go to questions' : nextLabel}
          onButtonClick={() =>
            isPreviewMode ? goToQuestionFromPreview() : moveToNextQuestion()
          }
        />
      )}

      {/* MCQ / comprehension */}
      {selectedQuestion && shouldUseMCQModal(selectedQuestion) && (
        <MCQModal
          open={isMCQModalOpen}
          businessColor={BUSINESS_COLOR}
          onClose={closeModals}
          title={
            selectedQuestion.type === 'COMPREHENSION'
              ? formatComprehensionTitleText(
                  selectedQuestion.name || selectedQuestion.title || 'Question',
                )
              : selectedQuestion.name || selectedQuestion.title || 'Question'
          }
          question={processQuestionText(selectedQuestion)}
          choices={getChoicesForQuestion(selectedQuestion)}
          onSubmit={() => moveToNextQuestion()}
          submitButtonText={nextLabel}
          questionIndex={(selectedQuestion.currentQuestionIndex || 0) + 1}
          totalQuestions={selectedQuestion.totalQuestionsInTest || 1}
          videoUrl={
            selectedQuestion.hasOwnMedia &&
            selectedQuestion.isVideo &&
            selectedQuestion.url !== 'undefined'
              ? selectedQuestion.url
              : undefined
          }
          imageSrc={
            selectedQuestion.hasOwnMedia && !selectedQuestion.isVideo
              ? selectedQuestion.url === 'undefined'
                ? undefined
                : selectedQuestion.url
              : undefined
          }
          imageAlt="MCQ Question"
          isMultiBlank={hasMultipleBlanks(selectedQuestion)}
          blankGroups={getBlankGroups(selectedQuestion)}
        />
      )}

      {/* Typed answer (fill-in-blanks / rearrange) */}
      {selectedQuestion && shouldUseTypedAnswerModal(selectedQuestion) && (
        <TypedAnswerModal
          open={isTypedAnswerModalOpen}
          question={selectedQuestion}
          submitText={nextLabel}
          onClose={closeModals}
          onSubmit={moveToNextQuestion}
        />
      )}
    </div>
  );
};

export default PlanPreviewPage;
